import { z } from 'zod';
import { CAPACITY_MAX } from '../limits.js';
import { EmailSchema, IsoTimestampSchema, SchemaVersionSchema } from './shared.js';
/**
 * T3-008B — US phone-number format validator: (XXX) XXX-XXXX.
 *
 * Strict regex anchored at start + end; rejects any other shape (no
 * international support in V1 per directive). Used at the application
 * boundary (admin UI submit, Zod parse on PATCH /user-profile) so the
 * stored phone always renders cleanly in the Quote PDF FROM block.
 */
export const PHONE_NUMBER_FORMAT = /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/;
export const PhoneNumberSchema = z
    .string()
    .regex(PHONE_NUMBER_FORMAT, 'Phone number must be in format (XXX) XXX-XXXX');
/**
 * Phase 10C — first-use onboarding flow steps, in order.
 *
 * `welcome` → `signature` → `tour` → `firstActivity` → `done`. Each step has a
 * matching optional ISO-timestamp field on `OnboardingCompletionStateSchema`
 * recording WHEN the user completed it. `done` is the terminal step; advancing
 * to it also flips `hasCompletedOnboarding` / `onboardingCompletedAt` server-side.
 */
export const ONBOARDING_STEPS = ['welcome', 'signature', 'tour', 'firstActivity', 'done'];
export const OnboardingStepSchema = z.enum(ONBOARDING_STEPS);
/**
 * Per-step completion timestamps for the onboarding flow. All fields optional —
 * an absent field means the step has not been completed. Stored as JSONB
 * (`onboarding_completion_state`); defaults to `{}` (no steps completed).
 */
export const OnboardingCompletionStateSchema = z
    .object({
    welcomeAt: IsoTimestampSchema.optional(),
    signatureAt: IsoTimestampSchema.optional(),
    tourAt: IsoTimestampSchema.optional(),
    firstActivityAt: IsoTimestampSchema.optional(),
    doneAt: IsoTimestampSchema.optional(),
})
    .default({});
// RIG-233 — sales-rep employment type. Drives the capacity readout (a full-time
// rep with a full book vs a part-time rep with room to take on more).
export const EMPLOYMENT_TYPES = ['full_time', 'part_time'];
export const EmploymentTypeSchema = z.enum(EMPLOYMENT_TYPES);
export const UserProfileSchema = z.object({
    id: z.string(),
    type: z.literal('user_profile'),
    schemaVersion: SchemaVersionSchema,
    email: EmailSchema,
    displayName: z.string().optional(),
    preferredTimezone: z.string().optional(),
    // T3-008B — sales rep phone number; rendered in Quote PDF FROM block when
    // set; FROM block omits the phone line entirely when null.
    phoneNumber: PhoneNumberSchema.nullable().optional(),
    // T3-014A — sales rep job title rendered beside displayName in PDF FROM
    // block (e.g., "Kenny Bevers, Founder & CEO"). Plain text; max 80 chars.
    title: z.string().max(80).nullable().optional(),
    // RIG-233 — sales-rep availability. employmentType: full_time | part_time
    // (null = unspecified). capacityTarget: the max active accounts the rep can
    // carry; the rep's CURRENT load is derived at read time from
    // accounts.assigned_rep_email (not stored here).
    employmentType: EmploymentTypeSchema.nullable().optional(),
    // QA 2026-10-01 F-08 — same ceiling as PATCH /me/profile.
    capacityTarget: z.number().int().nonnegative().max(CAPACITY_MAX).nullable().optional(),
    // 2026-11-02 — "Working accounts" switch (admin-set). false = not working
    // accounts: no new auto-assignments; their accounts appear in the Monday
    // unassigned digest. Does not affect sign-in, roles or commission.
    isActive: z.boolean().default(true),
    deactivatedAt: IsoTimestampSchema.optional(),
    deactivatedBy: z.string().optional(),
    emailSignatureHtml: z.string().default(''),
    emailSignatureUpdatedAt: IsoTimestampSchema.optional(),
    hasCompletedOnboarding: z.boolean().default(false),
    onboardingCompletedAt: IsoTimestampSchema.optional(),
    signaturePromptDismissedAt: IsoTimestampSchema.optional(),
    // Phase 10C — per-step onboarding progress. Defaults to `{}` (no steps done).
    // `hasCompletedOnboarding` stays the terminal flag; this is the breadcrumb.
    onboardingCompletionState: OnboardingCompletionStateSchema,
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    version: z.number().int().positive(),
});
//# sourceMappingURL=user-profile.js.map